import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Container, Table, Button, Alert, Spinner, Badge, Modal, Form } from "react-bootstrap";
import { FaEdit, FaTrash } from 'react-icons/fa';
import { useSelector } from "react-redux";

import VereinbarungService from "../services/vereinbarung.service";

const S8MyAgreements = () => {
    const [agreements, setAgreements] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    // Auswahl der freigegebenen Systemvorlagen beim Anlegen einer neuen Vereinbarung.
    const navigate = useNavigate();
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [templates, setTemplates] = useState([]);
    const [templatesLoading, setTemplatesLoading] = useState(false);
    const [selectedTemplateId, setSelectedTemplateId] = useState('');
    const [templateError, setTemplateError] = useState('');

    // Holt die ID des aktuellen Benutzers aus dem Redux-Store
    const { user: currentUser } = useSelector((state) => state.auth);

    useEffect(() => {
        setLoading(true);
        VereinbarungService.getAllAgreements()
            .then(response => {
                setAgreements(response.data);
                setLoading(false);
            })
            .catch(error => {
                const errorMessage = 
                    (error.response && error.response.data && error.response.data.message) ||
                    error.message ||
                    error.toString();
                setError(errorMessage);
                setLoading(false);
            });
    }, []);

    const handleDelete = (id) => {
        if (window.confirm('Sind Sie sicher, dass Sie diese Vereinbarung endgültig löschen möchten?')) {
            VereinbarungService.deleteAgreement(id)
                .then(() => {
                    // Liste neu laden, indem wir die agreements aus dem State filtern
                    setAgreements(agreements.filter(agreement => agreement.vereinbarung_id !== id));
                })
                .catch(err => {
                    const errorMessage = err.response?.data?.message || "Fehler beim Löschen.";
                    setError(errorMessage);
                });
        }
    };


    // Oeffnet die Vorlagen-Auswahl und laedt die fuer die Stufe freigegebenen
    // System-Vorlagen (Backend filtert bereits per visible_tiers).
    const handleOpenTemplateModal = () => {
        setTemplateError('');
        setSelectedTemplateId('');
        setShowTemplateModal(true);
        setTemplatesLoading(true);
        VereinbarungService.getAllAgreementTitles()
            .then((res) => {
                const sys = (res.data || []).filter((t) => t.is_system);
                setTemplates(sys);
                setTemplatesLoading(false);
            })
            .catch(() => {
                setTemplateError('Fehler beim Laden der freigegebenen Vorlagen.');
                setTemplatesLoading(false);
            });
    };

    const handleConfirmTemplate = () => {
        if (!selectedTemplateId) {
            setTemplateError('Bitte wählen Sie eine Systemvorlage aus.');
            return;
        }
        navigate(`/agreements/new?template=${selectedTemplateId}`);
    };

    const getStatusBadge = (status) => {
        switch (status) {
            case 'Entwurf':
                return <Badge bg="secondary">Entwurf</Badge>;
            case 'Unterzeichnet':
                return <Badge bg="success">Unterzeichnet</Badge>;
            case 'Archiviert':
                return <Badge bg="warning" text="dark">Archiviert</Badge>;
            default:
                return <Badge bg="info">{status}</Badge>;
        }
    };

    const renderContent = () => {
        if (loading) {
            return <div className="text-center"><Spinner animation="border" /> <p>Lade Vereinbarungen...</p></div>;
        }

        if (error) {
            return <Alert variant="danger">Fehler beim Laden der Vereinbarungen: {error}</Alert>;
        }

        if (agreements.length === 0) {
            return <Alert variant="info">Sie haben noch keine Vereinbarungen erstellt oder erhalten.</Alert>;
        }

        return (
            <Table striped bordered hover responsive>
                <thead>
                    <tr>
                        <th>Titel</th>
                        <th>Version</th>
                        <th>Status</th>
                        <th>Partner</th>
                        <th>Rolle</th>
                        <th>Aktionen</th>
                    </tr>
                </thead>
                <tbody>
                    {agreements.map(agreement => {
                        // Bestimmen, wer der "Partner" ist
                        const isCreator = agreement.ersteller.id === currentUser.id;
                        const partner = isCreator ? agreement.empfaenger : agreement.ersteller;

                        return (
                            <tr key={agreement.vereinbarung_id}>
                                <td>{agreement.individueller_titel ? agreement.individueller_titel : (agreement.titel ? agreement.titel.titel : 'N/A')}</td>
                                <td>v{agreement.version}</td>
                                <td>{getStatusBadge(agreement.status)}</td>
                                <td>{partner.username}</td>
                                <td>{isCreator ? 'Ersteller' : 'Empfänger'}</td>
                                
                               


                                <td>
                                    <Button as={Link} to={`/agreements/${agreement.vereinbarung_id}`} variant="primary" size="sm" className="me-2">
                                        Details anzeigen
                                    </Button>
                                    <Button as={Link} to={`/agreements/${agreement.vereinbarung_id}/edit`} variant="warning" size="sm" className="me-2" title="Bearbeiten" aria-label="Bearbeiten">
                                        <FaEdit />
                                    </Button>
                                    <Button variant="danger" size="sm" onClick={() => handleDelete(agreement.vereinbarung_id)} title="Löschen" aria-label="Löschen">
                                        <FaTrash />
                                    </Button>
                                </td>
                                
                                
                                
                              
                                
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        );
    };

    return (
        <Container>
            <div className="d-flex justify-content-between align-items-center my-3">
                <h1>Meine Vereinbarungen</h1>
                <Button variant="success" onClick={handleOpenTemplateModal}>
                    + Neue Vereinbarung
                </Button>
            </div>
            {renderContent()}

            <Modal show={showTemplateModal} onHide={() => setShowTemplateModal(false)}>
                <Modal.Header closeButton>
                    <Modal.Title>Systemvorlage auswählen</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="text-muted">
                        Wählen Sie die freigegebene Vorlage, auf der die neue Vereinbarung basieren soll.
                    </p>
                    {templateError && <Alert variant="danger">{templateError}</Alert>}
                    {templatesLoading ? (
                        <div className="text-center"><Spinner animation="border" /></div>
                    ) : templates.length === 0 ? (
                        <Alert variant="info">Für Ihre Stufe ist aktuell keine Systemvorlage freigegeben.</Alert>
                    ) : (
                        <Form.Select
                            value={selectedTemplateId}
                            onChange={(e) => setSelectedTemplateId(e.target.value)}
                            aria-label="Systemvorlage auswählen"
                        >
                            <option value="">Bitte Vorlage auswählen</option>
                            {templates.map((t) => (
                                <option key={t.titel_id} value={t.titel_id}>{t.titel}</option>
                            ))}
                        </Form.Select>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowTemplateModal(false)}>Abbrechen</Button>
                    <Button variant="primary" onClick={handleConfirmTemplate} disabled={templatesLoading || templates.length === 0}>
                        Weiter
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default S8MyAgreements;